import { useMemo } from "react";
import { SURVIVOR_LABELS, WITHIN_BATCH_DUP_MODE_LABELS } from "../../lib/pdoUiLabels.js";

/**
 * ADR-0094: discriminated shape for within-batch duplicate handling.
 * Default is `{ mode: 'error' }`. Opt-in to `{ mode: 'keep_one' }` requires
 * the user to explicitly name a tiebreaker column and survivor rule
 * (highest/lowest). There is no row-order fallback — survivor selection is
 * always explicit so reruns on the same input produce the same persisted state.
 */
export type DupHandling =
  | { mode: "error" }
  | { mode: "keep_one"; tiebreaker_column: string; survivor: "highest" | "lowest" };

type Props = {
  /** All upstream column names (raw incoming schema). */
  columnNames: string[];
  /** Selected key columns — excluded from the tiebreaker picker. */
  keyFields: string[];
  /** Optional Atlas column types (to restrict tiebreaker to numeric/timestamp/date — §10 OQ #2). */
  columnTypes?: Record<string, string>;

  value: DupHandling;
  onChange: (next: DupHandling) => void;

  disabled?: boolean;
  /** Called after blur from any inner control so the parent can flush an autosave. */
  onInteractionEnd?: () => void;
};

/** Atlas → JS type lanes that are sensibly comparable as tiebreakers. */
const TIEBREAKER_TYPES = new Set(["number", "date", "datetime", "timestamp"]);

export function WithinBatchDupSection(props: Props) {
  const { columnNames, keyFields, columnTypes, value, onChange, disabled, onInteractionEnd } = props;
  const end = onInteractionEnd ?? (() => {});

  const keySet = useMemo(() => new Set(keyFields), [keyFields]);

  // Non-key columns; if we know the types, further restrict to comparable lanes.
  const tiebreakerCandidates = useMemo(() => {
    return columnNames
      .filter((c) => !keySet.has(c))
      .filter((c) => {
        if (!columnTypes) return true;
        const t = (columnTypes[c] ?? "").toLowerCase();
        return TIEBREAKER_TYPES.has(t);
      })
      .sort((a, b) => a.localeCompare(b));
  }, [columnNames, keySet, columnTypes]);

  const isKeepOne = value.mode === "keep_one";
  const tiebreaker = isKeepOne ? value.tiebreaker_column : "";
  const survivor: "highest" | "lowest" = isKeepOne ? value.survivor : "highest";

  function setMode(next: "error" | "keep_one") {
    if (next === "error") {
      onChange({ mode: "error" });
      return;
    }
    // Default tiebreaker to first candidate (if any); survivor defaults to 'highest'.
    onChange({
      mode: "keep_one",
      tiebreaker_column: tiebreaker || tiebreakerCandidates[0] || "",
      survivor: survivor,
    });
  }

  function setTiebreaker(col: string) {
    if (value.mode !== "keep_one") return;
    onChange({ ...value, tiebreaker_column: col });
  }

  function setSurvivor(s: "highest" | "lowest") {
    if (value.mode !== "keep_one") return;
    onChange({ ...value, survivor: s });
  }

  const keepOneInvalid = isKeepOne && tiebreaker.trim().length === 0;

  return (
    <div className="field">
      <label className="label">Within-batch duplicates</label>
      <span className="help" style={{ display: "block", marginBottom: 6 }}>
        When the incoming result contains two rows with the same key.
      </span>

      <select
        className="select"
        value={value.mode}
        onChange={(e) => setMode(e.target.value as "error" | "keep_one")}
        onBlur={end}
        disabled={disabled}
      >
        <option value="error">{WITHIN_BATCH_DUP_MODE_LABELS.error}</option>
        <option value="keep_one">{WITHIN_BATCH_DUP_MODE_LABELS.keep_one}</option>
      </select>

      {isKeepOne ? (
        <div
          style={{
            display: "grid",
            gap: 8,
            marginTop: 8,
            paddingLeft: 8,
            borderLeft: "2px solid var(--border)",
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Tiebreaker column</label>
            {tiebreakerCandidates.length === 0 ? (
              <div className="help" style={{ color: "var(--danger-text)" }}>
                No non-key numeric / timestamp / date column available. Add one upstream or pick a different key.
              </div>
            ) : (
              <select
                className="select"
                value={tiebreaker}
                onChange={(e) => setTiebreaker(e.target.value)}
                onBlur={end}
                disabled={disabled}
              >
                <option value="">(pick a column)</option>
                {tiebreakerCandidates.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
            {keepOneInvalid ? (
              <div className="help" style={{ color: "var(--danger-text)" }}>
                Pick a tiebreaker column — runs will fail validation otherwise.
              </div>
            ) : null}
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Survivor rule</label>
            <select
              className="select"
              value={survivor}
              onChange={(e) => setSurvivor(e.target.value as "highest" | "lowest")}
              onBlur={end}
              disabled={disabled}
            >
              <option value="highest">{SURVIVOR_LABELS.highest}</option>
              <option value="lowest">{SURVIVOR_LABELS.lowest}</option>
            </select>
          </div>
        </div>
      ) : null}

      <div className="help" style={{ marginTop: 8 }}>
        Within-batch duplicates are resolved before checking the prior version. A
        row may be deduplicated within the batch and then skipped (Append) or
        overwritten (Merge) because its key already exists.
      </div>
    </div>
  );
}
